import { api, apiBytes, saveCsvFile } from "./client";

/*
 * The vendor portal (staff side), SMS and WhatsApp messaging, and custom modules.
 * Server: server/vendor-portal.js, server/messaging.js, server/custom-modules.js. Amounts are rupee strings.
 */

const get = <T,>(path: string, query?: Record<string, string | undefined>) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v) qs.set(k, v);
  const s = qs.toString();
  return api<T>("GET", s ? `${path}?${s}` : path);
};
const post = <T,>(path: string, body: unknown = {}) => api<T>("POST", path, body);
const patch = <T,>(path: string, body: unknown) => api<T>("PATCH", path, body);
const put = <T,>(path: string, body: unknown) => api<T>("PUT", path, body);
const del = <T = void,>(path: string) => api<T>("DELETE", path);

/** Saves a file the API serves as a download (an uploaded invoice). */
async function saveFile(path: string, fallback: string) {
  const res = await apiBytes("GET", path);
  const name = /filename\*=UTF-8''([^;]+)/.exec(res.headers.get("content-disposition") ?? "")?.[1];
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name ? decodeURIComponent(name) : fallback;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- vendor portal ---------- */

export type VendorPortalSettings = {
  enabled: boolean;
  allow_po_accept: boolean;
  allow_upload: boolean;
  allow_contact_update: boolean;
  allow_bank_update: boolean;
  show_payments: boolean;
  show_credits: boolean;
  headline: string | null;
  help_text: string | null;
};
export type PortalVendorRow = {
  id: string;
  name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  status: "active" | "inactive";
  portal: "on" | "off" | "none";
  invited_at: string | null;
  last_viewed_at: string | null;
  uploads_waiting: number;
  requests_waiting: number;
};
export type PortalAccess = { url: string; enabled: boolean; invited_at: string | null; invited_to: string | null; last_viewed_at: string | null; created_at: string };
export type VendorBank = { account_name: string; account_number_masked: string; ifsc: string; bank_name: string | null; updated_at: string; updated_by: string | null };
export type VendorUpload = {
  id: string;
  vendor_id: string;
  vendor_name: string | null;
  po_id: string | null;
  po_number: string | null;
  invoice_number: string;
  invoice_date: string | null;
  amount: string | null;
  note: string | null;
  file_name: string;
  content_type: string;
  size: number;
  status: "submitted" | "billed" | "declined";
  bill_id: string | null;
  bill_number: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  uploaded_at: string;
};
export type ChangeRequest = {
  id: string;
  vendor_id: string;
  vendor_name: string | null;
  kind: "contact" | "bank";
  data: Record<string, string | null>;
  status: "pending" | "applied" | "declined" | "superseded";
  requested_at: string;
  decided_at: string | null;
  decided_by: string | null;
  decision_note: string | null;
};
export type VendorPortalDetail = {
  vendor: { id: string; name: string; company_name: string | null; email: string | null; phone: string | null; address_line: string | null; city: string | null; pincode: string | null; status: string };
  access: PortalAccess | null;
  bank: VendorBank | null;
  po_responses: { po_id: string; po_number: string; response: "accepted" | "declined"; note: string | null; responded_at: string }[];
  uploads: VendorUpload[];
  requests: ChangeRequest[];
  events: { kind: string; body: string; by_name: string | null; at: string }[];
};
export type BillLineIn = { description: string; quantity: number | string; rate: string; gst_rate: number; hsn_sac?: string | null; account_id?: string };

export const vendorPortal = {
  settings: () => get<{ settings: VendorPortalSettings; upload_limit_mb: number }>("/admin/vendor-portal/settings"),
  /** Whether the portal is on: the Vendor portal screen's banner (purchases, not Settings). */
  status: () => get<{ enabled: boolean; upload_limit_mb: number }>("/admin/vendor-portal/status"),
  saveSettings: (b: Partial<VendorPortalSettings>) => put<{ settings: VendorPortalSettings; upload_limit_mb: number }>("/admin/vendor-portal/settings", b),
  vendors: (q?: string) => get<{ settings: VendorPortalSettings; vendors: PortalVendorRow[] }>("/admin/vendor-portal/vendors", { q }),
  vendor: (id: string) => get<VendorPortalDetail>(`/admin/vendor-portal/vendors/${id}`),
  setActive: (id: string, active: boolean) => post<VendorPortalDetail>(`/admin/vendor-portal/vendors/${id}/active`, { active }),
  invite: (id: string, email?: string) => post<VendorPortalDetail & { mail: { status: "sent" | "saved"; detail: string | null } }>(`/admin/vendor-portal/vendors/${id}/send`, email ? { email } : {}),
  resetLink: (id: string) => post<VendorPortalDetail>(`/admin/vendor-portal/vendors/${id}/reset`),
  revealBank: (id: string) => post<{ account_number: string }>(`/admin/vendor-portal/vendors/${id}/bank/reveal`),
  uploads: (status?: string) => get<VendorUpload[]>("/admin/vendor-portal/uploads", { status }),
  downloadUpload: (u: VendorUpload) => saveFile(`/admin/vendor-portal/uploads/${u.id}/download`, u.file_name),
  convertUpload: (id: string, b: { bill_number?: string; date?: string; due_date?: string | null; lines?: BillLineIn[] }) => post<{ upload: VendorUpload; bill: { id: string; bill_number: string } }>(`/admin/vendor-portal/uploads/${id}/convert`, b),
  declineUpload: (id: string, note: string) => post<VendorUpload>(`/admin/vendor-portal/uploads/${id}/decline`, { note }),
  requests: (status?: string) => get<ChangeRequest[]>("/admin/vendor-portal/requests", { status }),
  applyRequest: (id: string) => post<ChangeRequest>(`/admin/vendor-portal/requests/${id}/apply`),
  declineRequest: (id: string, note: string) => post<ChangeRequest>(`/admin/vendor-portal/requests/${id}/decline`, { note }),
};

/* ---------- messaging ---------- */

export type Channel = "sms" | "whatsapp";
export type MessagingStatus = {
  settings: { sms_requires_opt_in: boolean; enabled: boolean };
  choice_vars: Record<Channel, string>;
  active: Record<Channel, { key: string; label: string; note: string | null }>;
  providers: { key: string; label: string; channels: Channel[]; env: Partial<Record<Channel, string[]>>; ready: Partial<Record<Channel, boolean>> }[];
  events: { key: string; label: string }[];
  placeholders: { key: string; label: string; sample: string }[];
};
export type MessageTemplate = {
  id: string;
  channel: Channel;
  name: string;
  body: string;
  provider_template: string | null;
  language: string;
  enabled: boolean;
  placeholders: string[];
  sample: string;
  sms_segments: number | null;
  used_by: string[];
  updated_at: string;
};
export type MessageTemplateIn = Partial<Pick<MessageTemplate, "channel" | "name" | "body" | "provider_template" | "language" | "enabled">>;
export type MessageTrigger = { event: string; event_label: string; channel: Channel; template_id: string; template_name: string | null; enabled: boolean };
export type MessageContact = {
  kind: "customer" | "vendor";
  id: string;
  name: string;
  phone: string | null;
  phone_valid: boolean;
  sms: { allowed: boolean; opted_in_at: string | null; opted_out_at: string | null };
  whatsapp: { allowed: boolean; consent_at: string | null; consent_source: string | null; consent_by: string | null; opted_out_at: string | null };
};
export type MessageLog = {
  id: string;
  channel: Channel;
  provider: string | null;
  to: string | null;
  contact_kind: string | null;
  contact_id: string | null;
  contact_name: string | null;
  template_id: string | null;
  template_name: string | null;
  source: "trigger" | "workflow" | "manual";
  event: string | null;
  event_label: string | null;
  body: string | null;
  status: "queued" | "sending" | "sent" | "saved" | "failed" | "retrying" | "skipped";
  detail: string | null;
  attempts: number;
  next_retry_at: string | null;
  created_at: string;
  sent_at: string | null;
  created_by: string | null;
  // the provider's delivery receipt (server/messaging-inbound.js)
  delivery_status?: "queued" | "sent" | "delivered" | "read" | "failed" | null;
  delivered_at?: string | null;
  read_at?: string | null;
};

export const messaging = {
  status: () => get<MessagingStatus>("/admin/messaging/status"),
  saveSettings: (b: Partial<MessagingStatus["settings"]>) => put<MessagingStatus["settings"]>("/admin/messaging/settings", b),
  templates: () => get<MessageTemplate[]>("/admin/messaging/templates"),
  createTemplate: (b: MessageTemplateIn) => post<MessageTemplate>("/admin/messaging/templates", b),
  updateTemplate: (id: string, b: MessageTemplateIn) => patch<MessageTemplate>(`/admin/messaging/templates/${id}`, b),
  deleteTemplate: (id: string) => del(`/admin/messaging/templates/${id}`),
  triggers: () => get<MessageTrigger[]>("/admin/messaging/triggers"),
  saveTriggers: (triggers: { event: string; channel: Channel; template_id: string; enabled: boolean }[]) => put<MessageTrigger[]>("/admin/messaging/triggers", { triggers }),
  contacts: (q?: string, kind?: string) => get<MessageContact[]>("/admin/messaging/contacts", { q, kind }),
  consent: (b: { kind: string; id: string; channel: Channel; action: "opt_in" | "opt_out"; source?: string }) => post<MessageContact>("/admin/messaging/contacts/consent", b),
  log: (q: { status?: string; channel?: string; q?: string }) => get<MessageLog[]>("/admin/messaging/log", q),
  retry: (id: string) => post<MessageLog>(`/admin/messaging/log/${id}/retry`),
  send: (b: { channel: Channel; template_id: string; kind: string; id: string }) => post<MessageLog>("/admin/messaging/send", b),
};

/* ---------- custom modules ---------- */

export type CmFieldType = "text" | "textarea" | "number" | "date" | "dropdown" | "checkbox" | "email" | "url" | "lookup" | "formula";
export type CmLookup = "customers" | "vendors" | "items";
export type CmField = { key: string; label: string; type: CmFieldType; required?: boolean; unique?: boolean; options?: string[]; lookup?: CmLookup; formula?: string };
export type CmModule = {
  key: string;
  name: string;
  singular: string;
  description: string | null;
  fields: CmField[];
  list_columns: string[];
  status: "active" | "archived";
  permission_module: string;
  records: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};
export type CmModuleIn = { key?: string; name?: string; singular?: string; description?: string | null; fields?: CmField[]; list_columns?: string[]; status?: "active" | "archived" };
export type CmNavEntry = { key: string; name: string; singular: string; description: string | null; actions: ("view" | "create" | "edit" | "delete")[] };
export type CmValue = string | number | boolean | null;
export type CmRecord = { id: string; values: Record<string, CmValue>; labels: Record<string, string | null>; title: string; created_at: string; created_by: string | null; updated_at: string; updated_by: string | null };

export const customModules = {
  list: () => get<{ types: CmFieldType[]; lookups: CmLookup[]; limits: { modules: number; fields: number; columns: number }; modules: CmModule[] }>("/admin/custom-modules"),
  create: (b: CmModuleIn) => post<CmModule>("/admin/custom-modules", b),
  update: (key: string, b: CmModuleIn) => patch<CmModule>(`/admin/custom-modules/${key}`, b),
  remove: (key: string) => del(`/admin/custom-modules/${key}`),
  nav: () => get<CmNavEntry[]>("/admin/custom-modules/meta"),
  definition: (key: string) => get<CmModule & { actions: CmNavEntry["actions"] }>(`/admin/custom/${key}`),
  records: (key: string, q: { q?: string; sort?: string; dir?: string; offset?: string }) => get<{ total: number; limit: number; offset: number; records: CmRecord[] }>(`/admin/custom/${key}/records`, q),
  createRecord: (key: string, values: Record<string, CmValue>) => post<CmRecord>(`/admin/custom/${key}/records`, { values }),
  updateRecord: (key: string, id: string, values: Record<string, CmValue>) => patch<CmRecord>(`/admin/custom/${key}/records/${id}`, { values }),
  deleteRecord: (key: string, id: string) => del(`/admin/custom/${key}/records/${id}`),
  exportCsv: (key: string, q: { q?: string; sort?: string; dir?: string }) => saveCsvFile(`/admin/custom/${key}/export`, q),
  lookup: (key: string, kind: CmLookup, q: string) => get<{ id: string; label: string }[]>(`/admin/custom/${key}/lookup`, { kind, q }),
};
